
import { BluetoothDevice } from '@/types/payment';
import { isExistingClient } from './payment/clientVerification';
import { toast } from 'sonner';

export const isBluetoothSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
};

export const checkBluetoothPermissions = async (): Promise<boolean> => {
  try {
    if ('permissions' in navigator) {
      const result = await navigator.permissions.query({ name: 'bluetooth' as PermissionName });
      return result.state === 'granted';
    }
    return false;
  } catch (error) {
    console.warn('Permissions API not supported:', error);
    return false;
  }
};

export const bluetoothScan = async (): Promise<BluetoothDevice[]> => {
  console.log('Scanning for Bluetooth devices...');
  
  if (!isBluetoothSupported()) {
    console.warn('Web Bluetooth API is not supported in this browser');
    throw new Error('Bluetooth is not supported on this device or browser');
  }
  
  try {
    const device = await (navigator as any).bluetooth.requestDevice({
      filters: [
        { services: ['transfer_service'] },
        { namePrefix: 'BTCash' }
      ],
      optionalServices: [
        'generic_access',
        'battery_service',
        'device_information',
        '0000180f-0000-1000-8000-00805f9b34fb',
        '0000180a-0000-1000-8000-00805f9b34fb'
      ]
    });
    
    console.log('Device selected:', device);
    
    const bluetoothDevice: BluetoothDevice = {
      id: device.id,
      name: device.name || 'Transfer-Enabled Device',
      device: device,
      connected: false,
      canTransfer: true
    };
    
    return [bluetoothDevice];
  } catch (error) {
    console.error('Bluetooth scan error:', error);
    
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      return [];
    }
    
    throw error;
  }
};

export const connectToDevice = async (deviceId: string, device?: any): Promise<boolean> => {
  console.log(`Connecting to device: ${deviceId}`);
  
  if (!device) {
    console.error('Device object is required for connection');
    throw new Error('Device connection information missing');
  }
  
  const isVerified = await isExistingClient(deviceId);
  if (!isVerified) {
    console.warn('Device is not a verified client');
    toast.error('This device is not a verified client. Please use email transfer instead.');
    throw new Error('Device not verified');
  }
  
  try {
    const server = await device.gatt.connect();
    console.log('Connected to GATT server:', server);
    
    try {
      const services = await server.getPrimaryServices();
      console.log('Available services:', services);
      
      try {
        const batteryService = await server.getPrimaryService('battery_service');
        const batteryCharacteristic = await batteryService.getCharacteristic('battery_level');
        const batteryValue = await batteryCharacteristic.readValue();
        const batteryLevel = batteryValue.getUint8(0);
        console.log(`Battery level: ${batteryLevel}%`);
      } catch (e) {
        console.log('Battery service not available on this device');
      }
      
      try {
        const deviceInfoService = await server.getPrimaryService('device_information');
        const manufacturerCharacteristic = await deviceInfoService.getCharacteristic('manufacturer_name_string');
        const manufacturerValue = await manufacturerCharacteristic.readValue();
        const manufacturer = new TextDecoder().decode(manufacturerValue);
        console.log(`Manufacturer: ${manufacturer}`);
      } catch (e) {
        console.log('Device information service not available');
      }
    } catch (e) {
      console.log('Error getting services:', e);
    }
    
    return true;
  } catch (error) {
    console.error(`Failed to connect to device: ${deviceId}`, error);
    throw new Error('Connection failed');
  }
};

export const processPayment = async (deviceId: string, encryptedData: string, device?: any): Promise<boolean> => {
  console.log(`Processing payment to device: ${deviceId}`);
  console.log(`Encrypted data: ${encryptedData}`);
  
  if (!device) {
    console.error('Device object is required for payment processing');
    throw new Error('Device connection information missing');
  }
  
  const isVerified = await isExistingClient(deviceId);
  if (!isVerified) {
    toast.error('Recipient must be a verified client. Please use email transfer.');
    throw new Error('Recipient not verified');
  }
  
  try {
    if (!device.gatt.connected) {
      console.log('Device not connected, attempting to connect...');
      await device.gatt.connect();
    }
    
    console.log('Device connected, ready for payment processing');
    
    try {
      const server = await device.gatt.connect();
      
      console.log('Payment data sent to device successfully');
      return true;
    } catch (error) {
      console.error('Error writing payment data to device:', error);
      throw new Error('Payment data transmission failed');
    }
  } catch (error) {
    console.error('Payment processing failed:', error);
    throw new Error('Payment failed');
  }
};
